import { ImageAssetCard } from '@/components/Cards/ImageAssetCard';
import { useDeleteImage } from '@/hooks/useDeleteImage';
import { useGetBackgroundsList } from '@/hooks/useGetBackgroundsList';
import { logger } from '@/services/logger';
import theme from '@/theme';
import { IBackground, ImageType } from '@d-id/types';
import styled from '@emotion/styled';
import { useState } from 'react';

const StyledImageAssetCard = styled(ImageAssetCard)<{ selected: boolean }>`
    aspect-ratio: 16/9;
    flex-shrink: 0;
    ${({ selected }) =>
        selected &&
        `
        background: transparent !important;
        padding: 0;
        overflow: hidden;
        border: 1px solid ${theme.colors.primary.main};
    `}
`;

const UploadedBackgrounds = styled.div`
    gap: 11px;
    grid-template-columns: repeat(auto-fill, minmax(196px, calc((100% - 33px) / 4)));
    display: grid;
    width: 100%;

    @media (max-width: 912px) {
        grid-template-columns: repeat(auto-fill, minmax(196px, calc((100% - 11px) / 2)));
    }
`;

interface Props {
    value: string;
    onChange: (value: string) => void;
}

export const MyBackgrounds = ({ value, onChange }: Props) => {
    const { data: backgrounds } = useGetBackgroundsList();
    const { mutateAsync: deleteImage } = useDeleteImage(ImageType.Backgrounds);
    const [indexDeleting, setIndexDeleting] = useState(-1);

    const onBackgroundClick = (background: IBackground) => {
        background.poster && onChange(background.poster);
    };

    const handleDelete = async (backgroundToDelete: IBackground | undefined) => {
        if (!backgroundToDelete?.poster || !backgroundToDelete?.id || !value) {
            return;
        }

        try {
            if (value === backgroundToDelete?.poster) {
                onChange(theme.colors.white);
            }
            await deleteImage(backgroundToDelete?.id || '');
            setIndexDeleting(-1);
        } catch (error) {
            logger.error('Error deleting my background', { backgroundToDelete, error });
        }
    };

    return (
        <UploadedBackgrounds>
            {backgrounds?.map((background, index) => (
                <StyledImageAssetCard
                    key={index}
                    item={background}
                    variant={index === indexDeleting ? 'loading' : 'lite-border'}
                    src={background.thumbnail || background.poster}
                    onRemove={async () => {
                        setIndexDeleting(index);
                        handleDelete(background);
                    }}
                    onClick={async () => {
                        index !== indexDeleting && onBackgroundClick(background);
                    }}
                    selected={background.poster === value}
                />
            ))}
        </UploadedBackgrounds>
    );
};
